/** Agent wire events retain the backend's snake_case fields. */
export type AgentEvent<Content = { text?: string }, ToolResult = Record<string, unknown>> =
  | { type: 'token'; text: string }
  | { type: 'thinking'; text: string }
  | {
      type: 'tool_call_start'
      tool_call_id: string
      tool: string
      label: string
      args_summary: string
      icon: string
    }
  | {
      type: 'tool_call_end'
      tool_call_id: string
      result_summary: string
      error: string | null
      tool: string
      label: string
      icon: string
      result_data: ToolResult | null
    }
  /** The steps the agent says it will take; those before current_step, counted from 1, are done. note says why it
   * changed the plan, when it did. */
  | { type: 'plan'; steps: string[]; current_step: number; note?: string }
  | {
      type: 'confirmation_required'
      action_token: string
      tool: string
      summary: string
      payload: Record<string, unknown>
      current_state: Record<string, unknown> | null
      provenance: 'user_authored' | 'tool_output'
      pending_index: number
      pending_total: number
    }
  | { type: 'assistant_message'; chat_item_id: string; content: Content }
  | { type: 'error'; code: string; message: string }
  | { type: 'done' }

export function isAgentEvent<Content, ToolResult>(
  candidate: unknown
): candidate is AgentEvent<Content, ToolResult> {
  if (typeof candidate !== 'object' || candidate === null || !('type' in candidate)) return false
  switch (candidate.type) {
    case 'token':
    case 'thinking':
      return 'text' in candidate && typeof candidate.text === 'string'
    case 'plan':
      return (
        'steps' in candidate &&
        Array.isArray(candidate.steps) &&
        candidate.steps.every((step) => typeof step === 'string') &&
        'current_step' in candidate &&
        typeof candidate.current_step === 'number' &&
        (!('note' in candidate) || typeof candidate.note === 'string')
      )
    case 'tool_call_start':
    case 'tool_call_end':
    case 'confirmation_required':
    case 'assistant_message':
    case 'error':
    case 'done':
      return true
    default:
      return false
  }
}
